// Product catalog for Ostrich Foods
// Each entry has: id, name, category, tagline, description (long), benefits[], usage, gallery[], image, prices, applications.
// Main `image` is the branded pouch in /public/products/{id}.png.
// `gallery` adds 2 lifestyle/ingredient photos for the detail page.

export const CATEGORIES = ["All", "Vegetable", "Fruit", "Spice", "Wellness"];

const lifestyle = (a, b) => [a, b];

export const PRODUCTS = [
    {
        id: "onion-powder",
        name: "Onion Powder",
        category: "Vegetable",
        tagline: "Pungent, sun-dried, finely milled",
        description:
            "Made from carefully selected white onions, washed, sliced, and gently dehydrated, then milled to a fine, free-flowing powder. A 1 kg of our powder represents roughly 8 kg of fresh onions — concentrated convenience for any kitchen or food formulator. It rehydrates instantly and disperses cleanly through dry mixes, brines, and sauces.",
        benefits: [
            "Rich in quercetin — a natural antioxidant",
            "Supports heart health and immunity",
            "Adds savoury depth without raw-onion bite",
            "Long shelf life — no refrigeration needed",
        ],
        usage: "Soup mixes, sauces, dry rubs, snack seasonings, instant noodles, marinades. Use 1 tsp powder = 1 small fresh onion.",
        image: "/products/onion-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/175689/pexels-photo-175689.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/4197442/pexels-photo-4197442.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 240, "500g": 450, "1kg": 840 },
        applications: ["Seasonings", "Snacks", "Sauces"],
    },
    {
        id: "garlic-powder",
        name: "Garlic Powder",
        category: "Vegetable",
        tagline: "Bold aroma, clean kitchen finish",
        description:
            "Pure garlic clove powder — fresh white bulbs are peeled, sliced, dried at low temperatures and stone-milled. Industry favourite for marinades, dry rubs and culinary blends. No anti-caking agents, no carriers — only garlic.",
        benefits: [
            "Allicin-rich — natural antimicrobial",
            "Supports cardiovascular health",
            "Bold flavour with consistent dosing",
            "Halal · Kosher · Gluten-free certified",
        ],
        usage: "Marinades, sauces, bakery, ready-meal seasonings. Use 1/8 tsp powder = 1 fresh clove.",
        image: "/products/garlic-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/4197442/pexels-photo-4197442.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/4197444/pexels-photo-4197444.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 260, "500g": 500, "1kg": 940 },
        applications: ["Marinades", "Bakery", "Nutraceuticals"],
    },
    {
        id: "tomato-powder",
        name: "Tomato Powder",
        category: "Vegetable",
        tagline: "Concentrated umami, vine-red colour",
        description:
            "Sun-ripened tomatoes are pulped, spray-dried, and milled to a fine red powder. Rehydrates instantly into purée — ideal for soup mixes, pasta sauces and instant meals. No salt added.",
        benefits: [
            "Lycopene-rich antioxidant",
            "Natural umami without MSG",
            "Concentrated colour for clean labels",
            "Vegan, gluten-free, no preservatives",
        ],
        usage: "Pasta sauces, soup mixes, ketchup blends, ready-meals. Use 2 tbsp powder + 1/2 cup water = 1 cup purée.",
        image: "/products/tomato-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/1327838/pexels-photo-1327838.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/263876/pexels-photo-263876.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 290, "500g": 540, "1kg": 1010 },
        applications: ["Sauces", "Instant meals", "Soups"],
    },
    {
        id: "carrot-powder",
        name: "Carrot Powder",
        category: "Vegetable",
        tagline: "Beta-carotene rich, naturally sweet",
        description:
            "Cold-dried carrot powder retains the colour and nutrition of fresh carrots. Used in baby food, juices, baking and natural colouring applications. No added sugar or sweeteners.",
        benefits: [
            "High in beta-carotene (Vitamin A)",
            "Supports eye health & immunity",
            "Naturally sweet — gentle for kids",
            "Natural orange colourant",
        ],
        usage: "Baby cereals, smoothies, breads, pasta, soups. 1 tbsp powder ≈ 1 medium carrot.",
        image: "/products/carrot-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/143133/pexels-photo-143133.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/2238343/pexels-photo-2238343.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 250, "500g": 470, "1kg": 880 },
        applications: ["Baby food", "Bakery", "Natural colour"],
    },
    {
        id: "spinach-powder",
        name: "Spinach Powder",
        category: "Vegetable",
        tagline: "Iron-forward, deep emerald green",
        description:
            "Whole-leaf spinach gently dehydrated to retain chlorophyll and minerals. Loved by smoothie & nutraceutical brands. No fillers, no flow agents.",
        benefits: [
            "High in iron, magnesium, folate",
            "Supports energy and red blood cells",
            "Natural green colourant",
            "Plant-based protein source",
        ],
        usage: "Smoothies, pasta dough, breads, sauces, supplement capsules. 1 tsp = ~1 cup fresh leaves.",
        image: "/products/spinach-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/2255935/pexels-photo-2255935.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/3338497/pexels-photo-3338497.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 310, "500g": 580, "1kg": 1090 },
        applications: ["Greens blends", "Pasta", "Wellness"],
    },
    {
        id: "beetroot-powder",
        name: "Beetroot Powder",
        category: "Vegetable",
        tagline: "Nitrate-rich, vivid magenta",
        description:
            "Stone-ground beetroot powder — a pre-workout staple and a clean natural colourant for bakery and confectionery. Made from fresh red beets dehydrated at low temperature.",
        benefits: [
            "Natural nitrates support stamina & blood flow",
            "Pre-workout & athletic support",
            "Vibrant magenta colour for bakery & cosmetics",
            "Detox & liver-friendly",
        ],
        usage: "Pre-workout drinks, smoothies, lattes, baked goods, cosmetic masks. 1 tsp ≈ 1/4 small beet.",
        image: "/products/beetroot-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/2286776/pexels-photo-2286776.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/9203644/pexels-photo-9203644.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 350, "500g": 660, "1kg": 1230 },
        applications: ["Pre-workout", "Bakery", "Cosmetics"],
    },
    {
        id: "ginger-powder",
        name: "Ginger Powder",
        category: "Spice",
        tagline: "Warm, sharp, ayurvedic classic",
        description:
            "Sun-dried mature ginger root, cryo-milled to lock in essential oils. A staple for nutraceutical, beverage and wellness blends.",
        benefits: [
            "Anti-inflammatory & digestive aid",
            "Soothes nausea and cold symptoms",
            "Ayurvedic & Ayurveda-approved",
            "Warming, immune-supportive spice",
        ],
        usage: "Tea blends, golden milk, marinades, gingerbread, capsules. 1/2 tsp powder = 1 tsp fresh grated.",
        image: "/products/ginger-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/161556/ginger-plant-asia-rhizome-161556.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/161536/ginger-tea-mug-mint-161536.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 320, "500g": 590, "1kg": 1120 },
        applications: ["Wellness", "Bakery", "Ayurveda"],
    },
    {
        id: "potato-powder",
        name: "Potato Powder",
        category: "Vegetable",
        tagline: "Silky texture, neutral profile",
        description:
            "Dehydrated potato — perfect for instant mashed mixes, soups, snack coatings and gluten-free formulations. Rehydrates to creamy consistency in minutes.",
        benefits: [
            "Naturally gluten-free starch",
            "Excellent thickener & binder",
            "Quick energy from complex carbs",
            "Neutral flavour — won't compete with seasoning",
        ],
        usage: "Instant mash, gluten-free baking, snack coatings, soup thickener. 1 cup powder + 2 cups water = mash.",
        image: "/products/potato-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/144248/potatoes-vegetables-erdfrucht-bio-144248.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/4079523/pexels-photo-4079523.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 220, "500g": 420, "1kg": 790 },
        applications: ["Instant meals", "Snacks", "Bakery"],
    },
    {
        id: "banana-powder",
        name: "Banana Powder",
        category: "Fruit",
        tagline: "Naturally sweet, potassium-loaded",
        description:
            "Dried from sun-ripened cavendish bananas. Excellent in smoothies, baby cereals, energy bars and protein blends. No added sugar — just fruit.",
        benefits: [
            "Rich in potassium for muscle recovery",
            "Natural sweetener — no sugar added",
            "Gentle on digestion (great for baby food)",
            "Long shelf life vs fresh fruit",
        ],
        usage: "Smoothies, baby cereal, energy bars, banana bread, ice cream. 2 tbsp = 1 small banana.",
        image: "/products/banana-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/2872755/pexels-photo-2872755.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/1093038/pexels-photo-1093038.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 300, "500g": 560, "1kg": 1040 },
        applications: ["Smoothies", "Baby food", "Protein"],
    },
    {
        id: "mango-powder",
        name: "Mango Powder",
        category: "Fruit",
        tagline: "Alphonso sweetness, golden glow",
        description:
            "Sweet, tangy and fragrant — produced from ripe Indian mangoes. The signature note for tropical bakery and beverage. No added flavours.",
        benefits: [
            "Vitamin A & C powerhouse",
            "Natural tropical flavour",
            "Aids digestion (enzyme-rich)",
            "Vegan, gluten-free, kosher-friendly",
        ],
        usage: "Beverage mixes, lassi, ice cream, cakes, kulfi. 1 tbsp = 1/4 cup ripe mango pulp.",
        image: "/products/mango-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/918643/pexels-photo-918643.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/2294471/pexels-photo-2294471.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 400, "500g": 750, "1kg": 1410 },
        applications: ["Beverage", "Bakery", "Confectionery"],
    },
    {
        id: "apple-powder",
        name: "Apple Powder",
        category: "Fruit",
        tagline: "Pectin-rich, gentle sweetness",
        description:
            "Whole-fruit apple powder including skin for fibre and pectin. A pantry hero for cereals, bars and baby blends.",
        benefits: [
            "Pectin-rich — natural fibre",
            "Gentle apple flavour for kids",
            "Antioxidant polyphenols (with skin)",
            "No added sugar or preservatives",
        ],
        usage: "Cereals, granola, baby food, oatmeal, jam blends. 2 tbsp = 1/2 cup chopped apple.",
        image: "/products/apple-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/206959/pexels-photo-206959.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/162659/apple-fruit-red-juicy-delicious-162659.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 350, "500g": 660, "1kg": 1250 },
        applications: ["Cereals", "Bakery", "Wellness"],
    },
    {
        id: "guava-powder",
        name: "Guava Powder",
        category: "Fruit",
        tagline: "Vitamin C bomb, tropical aroma",
        description:
            "Pink guava powder, naturally tart and aromatic. Lifts beverages, sorbets and cosmetic formulations.",
        benefits: [
            "Exceptionally high in Vitamin C",
            "Tropical pink colour — natural",
            "Skin-nourishing antioxidants",
            "Tart-sweet balance",
        ],
        usage: "Smoothies, sorbets, cosmetic masks, candies. 1 tbsp = 1/4 cup guava pulp.",
        image: "/products/guava-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/2294471/pexels-photo-2294471.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/3030547/pexels-photo-3030547.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 370, "500g": 700, "1kg": 1320 },
        applications: ["Beverage", "Cosmetics", "Confectionery"],
    },
    {
        id: "papaya-powder",
        name: "Papaya Powder",
        category: "Fruit",
        tagline: "Enzymatic, skin-loving, sunny orange",
        description:
            "Rich in papain enzyme. Used widely in nutraceuticals, digestive blends, skincare masks and natural colouring.",
        benefits: [
            "Papain enzyme aids digestion",
            "Skin brightening — beauty staple",
            "Natural source of Vitamin C",
            "Anti-inflammatory support",
        ],
        usage: "Smoothies, digestive blends, skincare masks, marinade tenderiser. Use 1 tsp/serving.",
        image: "/products/papaya-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/4113835/pexels-photo-4113835.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/1414122/pexels-photo-1414122.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 360, "500g": 680, "1kg": 1290 },
        applications: ["Nutraceuticals", "Cosmetics", "Beverage"],
    },
    {
        id: "pineapple-powder",
        name: "Pineapple Powder",
        category: "Fruit",
        tagline: "Bromelain-rich, bright tang",
        description:
            "Spray-dried from golden pineapple — a clean, single-ingredient powder for beverage, bakery and dessert applications.",
        benefits: [
            "Bromelain enzyme aids digestion",
            "Bright, tropical tang",
            "Anti-inflammatory support",
            "Natural meat tenderiser",
        ],
        usage: "Tropical drinks, marinades, ice cream, smoothies. 1 tbsp = 1/4 cup fresh pineapple.",
        image: "/products/pineapple-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/947879/pexels-photo-947879.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/1907227/pexels-photo-1907227.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 390, "500g": 730, "1kg": 1360 },
        applications: ["Beverage", "Bakery", "Marinades"],
    },
    {
        id: "lemon-powder",
        name: "Lemon Powder",
        category: "Fruit",
        tagline: "Whole-fruit zing, no preservatives",
        description:
            "Whole lemon powder including peel for natural oils. A clean substitute for lemon zest in seasonings and confectionery.",
        benefits: [
            "Vitamin C & natural citric acid",
            "Whole-fruit nutrition (with peel)",
            "Detox & alkalising support",
            "Bright tang for both savoury and sweet",
        ],
        usage: "Drinks, salad dressings, baked goods, seasoning rubs. 1 tsp = juice of 1 medium lemon.",
        image: "/products/lemon-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/1414110/pexels-photo-1414110.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/952360/pexels-photo-952360.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 330, "500g": 620, "1kg": 1160 },
        applications: ["Beverage", "Bakery", "Seasonings"],
    },
    {
        id: "jackfruit-powder",
        name: "Jackfruit Powder",
        category: "Fruit",
        tagline: "Plant-protein star, low GI",
        description:
            "Made from green jackfruit — the rising plant-protein ingredient. Used in mock-meat, bakery and blood-sugar friendly mixes.",
        benefits: [
            "Plant-based protein source",
            "Low glycemic index — diabetic-friendly",
            "Rich in fibre and potassium",
            "Mock-meat ingredient star",
        ],
        usage: "Plant-protein bars, mock-meat blends, bakery, smoothies. 1/4 cup powder = 1 cup cooked jackfruit.",
        image: "/products/jackfruit-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/5945763/pexels-photo-5945763.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/8472939/pexels-photo-8472939.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 420, "500g": 790, "1kg": 1500 },
        applications: ["Plant protein", "Bakery", "Wellness"],
    },
    {
        id: "ashwagandha-powder",
        name: "Ashwagandha Powder",
        category: "Wellness",
        tagline: "Adaptogenic root, ancient calm",
        description:
            "Stone-milled ashwagandha (Withania somnifera) root from Indian-grown herbs. Ayurveda's most trusted adaptogen — supports stress response, sleep and immunity. Single-origin, no fillers, no bulking agents.",
        benefits: [
            "Adaptogenic — helps body handle stress",
            "Supports restful sleep & energy",
            "Boosts immunity and vitality",
            "Ayurveda-grade, withanolide-rich",
        ],
        usage: "Golden milk, evening tea, smoothies, supplement capsules. 1/2 tsp (~3 g) once or twice a day with warm milk or water.",
        image: "/products/ashwagandha-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/3735218/pexels-photo-3735218.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/4110251/pexels-photo-4110251.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 390, "500g": 730, "1kg": 1360 },
        applications: ["Wellness", "Ayurveda", "Nutraceuticals"],
    },
    {
        id: "moringa-powder",
        name: "Moringa Powder",
        category: "Wellness",
        tagline: "The miracle tree, bright green",
        description:
            "Shade-dried moringa (Moringa oleifera) leaves, stone-milled into a vivid emerald powder. Packed with vitamins A, C, calcium, iron and plant protein — a genuine superfood staple.",
        benefits: [
            "Rich in vitamins A, C, E and iron",
            "Plant-protein & antioxidant powerhouse",
            "Anti-inflammatory & energy-boosting",
            "Supports healthy skin & hair",
        ],
        usage: "Smoothies, juices, green drinks, soups, pesto. 1 tsp (~2 g) daily — start small, earthy flavour.",
        image: "/products/moringa-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/1346155/pexels-photo-1346155.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/461382/pexels-photo-461382.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 350, "500g": 660, "1kg": 1230 },
        applications: ["Greens blends", "Smoothies", "Wellness"],
    },
    {
        id: "red-chilli-powder",
        name: "Red Chilli Powder",
        category: "Spice",
        tagline: "Bold, fiery, Indian-grown",
        description:
            "Stone-milled red chilli powder from sun-dried Indian red chillies — clean heat, deep crimson colour, no added oil or colour. The everyday tadka and curry essential, made the way it should be.",
        benefits: [
            "Capsaicin — boosts metabolism",
            "Rich source of vitamin A & C",
            "No artificial colour or filler",
            "Long shelf life when sealed",
        ],
        usage: "Curries, dals, tadka, marinades, rubs, pickles. 1/2 – 1 tsp per dish to taste.",
        image: "/products/red-chilli-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/39288/red-chilli-pepper-vegetables-spicy-39288.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/33320/chilli-pepper-spicy-red-chili.jpg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 290, "500g": 540, "1kg": 1010 },
        applications: ["Curries", "Tadka", "Marinades"],
    },
    {
        id: "green-chilli-powder",
        name: "Green Chilli Powder",
        category: "Spice",
        tagline: "Fresh, sharp, dehydrated heat",
        description:
            "Whole green chillies dehydrated at low temperature to lock in their fresh herbaceous flavour, then finely milled. A vibrant emerald powder that delivers fresh-chilli punch without the chopping.",
        benefits: [
            "High in vitamin C & antioxidants",
            "Fresh chilli flavour, no chopping",
            "Capsaicin without added oil or salt",
            "Pairs with curd, marinades, chaat",
        ],
        usage: "Marinades, curd dips, chaat masala, sandwiches, dosa batter, instant pickles. 1/4 – 1/2 tsp to taste.",
        image: "/products/green-chilli-powder.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/4198018/pexels-photo-4198018.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/4198015/pexels-photo-4198015.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 320, "500g": 600, "1kg": 1120 },
        applications: ["Marinades", "Chaat", "Dips"],
    },
    {
        id: "green-chilli-flakes",
        name: "Green Chilli Flakes",
        category: "Spice",
        tagline: "Sun-dried heat, coarse green flakes",
        description:
            "Fresh green chillies gently dehydrated and coarsely crushed — bright green flakes with visible seeds that deliver a herbaceous chilli punch without the mess of chopping. A vibrant finishing spice for pizzas, pastas, dips and Indian tadka.",
        benefits: [
            "Bright green colour, fresh chilli aroma",
            "Vitamin C & capsaicin rich",
            "No added oil, colour or preservatives",
            "Coarse flakes — visible chilli & seeds",
        ],
        usage: "Pizza, pasta, dips, eggs, chaat, tadka, chilli oil. 1/4 – 1/2 tsp to taste.",
        image: "/products/green-chilli-flakes.png",
        gallery: lifestyle(
            "https://images.pexels.com/photos/4198018/pexels-photo-4198018.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/4198015/pexels-photo-4198015.jpeg?auto=compress&cs=tinysrgb&w=1200",
        ),
        prices: { "250g": 340, "500g": 640, "1kg": 1200 },
        applications: ["Pizza/pasta", "Tadka", "Dips"],
    },
];

export const PACK_SIZES = ["250g", "500g", "1kg"];

export const COUPON = {
    code: "AVAIL10",
    discount: 0.10, // 10%
    message: "10% off applied — code AVAIL10",
};
